import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import FocusLock from "react-focus-lock";
import { useTranslation } from "react-i18next";

import ProductPicker from "../../../components/catalog/pickers/ProductPicker";
import PendingStockOperationNotice from "../../../components/stock/PendingStockOperationNotice";
import {
  applyStockValueAdjustment,
  fetchStockProductContext,
  previewStockValueAdjustment,
  searchStockProducts,
} from "../../../services/stockService";

const MODES = ["target_value", "target_average_cost", "value_delta"];
const numberValue = (value) => String(value ?? "").replace(",", ".").trim();
function money(value) {
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(2)} PLN` : "—";
}
function decimal(value, digits = 6) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(digits).replace(/\.?0+$/, "") : "—";
}
function productUnit(product) { return product?.stockUnit?.symbol || product?.stock_unit_code || ""; }

function uniquePositiveBatches(context) {
  const map = new Map();
  const sourceRows = Array.isArray(context?.batch_options) && context.batch_options.length
    ? context.batch_options.map((batch) => ({ batch }))
    : (context?.batches?.items || []);
  for (const row of sourceRows) {
    const batch = row?.batch;
    const cost = batch?.cost;
    if (!batch?.id || !cost || Number(cost.total_quantity || 0) <= 0) continue;
    if (!map.has(String(batch.id))) map.set(String(batch.id), {
      id: String(batch.id),
      batch_number: batch.batch_number || `#${batch.id}`,
      expiry_date: batch.expiry_date || null,
      quantity: cost.total_quantity,
      value_net: cost.total_value_net,
      average_cost_net: cost.average_cost_net,
    });
  }
  return [...map.values()];
}

export default function StockValueAdjustmentDialog({ open, initialProduct = null, onClose, onApplied }) {
  const { t } = useTranslation(["stock", "common"]);
  const [product, setProduct] = useState(initialProduct);
  const [context, setContext] = useState(null);
  const [batchId, setBatchId] = useState("");
  const [inputMode, setInputMode] = useState("target_value");
  const [inputValue, setInputValue] = useState("");
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewFingerprint, setPreviewFingerprint] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingContext, setLoadingContext] = useState(false);
  const [error, setError] = useState("");

  const batchMode = product?.stock_valuation_method === "batch_moving_average";
  const batches = useMemo(() => uniquePositiveBatches(context), [context]);
  const selectedBatch = batches.find((row) => row.id === String(batchId)) || null;
  const unit = productUnit(product);

  const payload = useMemo(() => {
    const result = {
      product_id: product?.id ? String(product.id) : null,
      batch_id: batchMode ? (batchId || null) : null,
      input_mode: inputMode,
      target_value_net: null,
      target_average_cost_net: null,
      value_delta_net: null,
      reason: reason.trim(),
      comment: comment.trim(),
    };
    if (inputMode === "target_value") result.target_value_net = numberValue(inputValue);
    if (inputMode === "target_average_cost") result.target_average_cost_net = numberValue(inputValue);
    if (inputMode === "value_delta") result.value_delta_net = numberValue(inputValue);
    return result;
  }, [product, batchMode, batchId, inputMode, inputValue, reason, comment]);
  const fingerprint = JSON.stringify(payload);
  const previewCurrent = Boolean(preview && previewFingerprint === fingerprint);

  useEffect(() => {
    if (!open) return;
    setProduct(initialProduct || null);
    setContext(null); setBatchId(""); setInputMode("target_value"); setInputValue("");
    setReason(""); setComment(""); setPreview(null); setPreviewFingerprint(""); setError("");
  }, [open, initialProduct]);

  useEffect(() => {
    if (!open || !product?.id) { setContext(null); return undefined; }
    const controller = new AbortController();
    setLoadingContext(true); setError(""); setPreview(null); setPreviewFingerprint("");
    fetchStockProductContext(product.id, { batch_page: 1, batch_page_size: 100, include_zero: false }, { signal: controller.signal })
      .then((result) => {
        setContext(result);
        if (result?.product?.id) {
          setProduct((current) => String(current?.id || "") === String(result.product.id) ? { ...current, ...result.product } : current);
        }
      })
      .catch((err) => { if (err?.code !== "ERR_CANCELED") setError(err?.message || t("common:errors.actionFailed")); })
      .finally(() => setLoadingContext(false));
    return () => controller.abort();
  }, [open, product?.id, t]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const key = (event) => {
      if (event.key === "Escape" && !busy) { event.preventDefault(); onClose?.(); }
    };
    document.addEventListener("keydown", key);
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener("keydown", key); };
  }, [open, busy, onClose]);

  if (!open) return null;

  const invalidate = () => { setPreview(null); setPreviewFingerprint(""); setError(""); };
  const setProductSafe = (next) => { setProduct(next || null); setBatchId(""); setInputValue(""); invalidate(); };
  const translatedError = (err) => t(`stock:valueAdjustment.codes.${err?.code}`, { defaultValue: err?.data?.message || err?.message || t("common:errors.actionFailed") });

  async function runPreview() {
    if (!product?.id || !inputValue.trim() || !reason.trim() || !comment.trim() || (batchMode && !batchId)) {
      setError(t("stock:valueAdjustment.completeRequired")); return;
    }
    setBusy(true); setError("");
    try {
      const result = await previewStockValueAdjustment(payload);
      setPreview(result); setPreviewFingerprint(fingerprint);
    } catch (err) { setError(translatedError(err)); }
    finally { setBusy(false); }
  }

  async function apply() {
    if (!previewCurrent) return;
    setBusy(true); setError("");
    try {
      const result = await applyStockValueAdjustment({ ...payload, expected_before: preview.preview_guard });
      await onApplied?.(result);
    } catch (err) { setError(translatedError(err)); }
    finally { setBusy(false); }
  }

  const currentBasis = batchMode ? selectedBatch : {
    quantity: product?.global_stock_quantity,
    value_net: product?.global_stock_value_net,
    average_cost_net: product?.average_cost_net,
  };
  const inputLabel = t(`stock:valueAdjustment.modes.${inputMode}.field`);

  return createPortal(
    <div className="stock-dialog-backdrop" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}>
      <FocusLock returnFocus>
        <div className="stock-audit-dialog stock-correction-dialog stock-value-adjustment-dialog" role="dialog" aria-modal="true" aria-labelledby="stock-wom-title">
          <header className="stock-audit-dialog__header">
            <div>
              <span className="stock-dialog__eyebrow">{t("stock:valueAdjustment.eyebrow")}</span>
              <h2 id="stock-wom-title">{t("stock:valueAdjustment.title")}</h2>
              <p>{t("stock:valueAdjustment.subtitle")}</p>
            </div>
            <button type="button" className="stock-dialog__close" disabled={busy} onClick={onClose} aria-label={t("common:actions.close")}>×</button>
          </header>

          <div className="stock-audit-dialog__body">
            <PendingStockOperationNotice scope="stock" onResolved={onApplied} />
            {error && <div className="stock-alert stock-alert--error">{error}</div>}
            <section className="stock-correction-notice">
              <strong>{t("stock:valueAdjustment.currentTitle")}</strong>
              <span>{t("stock:valueAdjustment.currentHelp")}</span>
            </section>

            <section className="stock-audit-detail__section">
              <h3>{t("stock:valueAdjustment.stockTitle")}</h3>
              <ProductPicker
                selectedProduct={product}
                onSelect={setProductSafe}
                searchProducts={searchStockProducts}
                context="stock"
                disabled={busy}
                helperText={t("stock:valueAdjustment.productHelp")}
              />
              {product && batchMode && (
                <label className="stock-form-field stock-value-adjustment-batch">
                  {t("stock:valueAdjustment.batch")}
                  <select value={batchId} disabled={busy || loadingContext} onChange={(e) => { setBatchId(e.target.value); invalidate(); }}>
                    <option value="">{t("stock:valueAdjustment.selectBatch")}</option>
                    {batches.map((row) => <option key={row.id} value={row.id}>{row.batch_number}</option>)}
                  </select>
                  <small>{t("stock:valueAdjustment.batchHelp")}</small>
                </label>
              )}
              {product && !loadingContext && (
                <div className="stock-correction-summary stock-value-adjustment-basis">
                  <div><span>{t("stock:valueAdjustment.currentQuantity")}</span><strong>{decimal(currentBasis?.quantity)} {unit}</strong></div>
                  <div><span>{t("stock:valueAdjustment.currentValue")}</span><strong>{money(currentBasis?.value_net)}</strong></div>
                  <div><span>{t("stock:valueAdjustment.currentAverage")}</span><strong>{decimal(currentBasis?.average_cost_net, 6)} PLN/{unit}</strong></div>
                  <div><span>{t("stock:valueAdjustment.scope")}</span><strong>{batchMode ? (selectedBatch?.batch_number || "—") : t("stock:valueAdjustment.productScope")}</strong></div>
                </div>
              )}
            </section>

            <section className="stock-audit-detail__section">
              <h3>{t("stock:valueAdjustment.inputTitle")}</h3>
              <div className="stock-value-adjustment-modes" role="radiogroup">
                {MODES.map((mode) => (
                  <label key={mode} className={inputMode === mode ? "active" : ""}>
                    <input type="radio" name="wom-mode" value={mode} checked={inputMode === mode} disabled={busy} onChange={() => { setInputMode(mode); setInputValue(""); invalidate(); }} />
                    <span><strong>{t(`stock:valueAdjustment.modes.${mode}.label`)}</strong><small>{t(`stock:valueAdjustment.modes.${mode}.help`)}</small></span>
                  </label>
                ))}
              </div>
              <div className="stock-correction-grid stock-value-adjustment-inputs">
                <label className="stock-form-field">
                  {inputLabel}
                  <input type="text" inputMode="decimal" value={inputValue} disabled={busy} onChange={(e) => { setInputValue(e.target.value); invalidate(); }} />
                  <small>{inputMode === "target_average_cost" ? `PLN/${unit}` : "PLN"}</small>
                </label>
                <label className="stock-form-field">
                  {t("stock:fields.reason")}
                  <input type="text" maxLength="120" value={reason} disabled={busy} onChange={(e) => { setReason(e.target.value); invalidate(); }} />
                </label>
                <label className="stock-form-field stock-correction-field--wide">
                  {t("stock:fields.comment")}
                  <textarea rows="3" maxLength="4000" value={comment} disabled={busy} onChange={(e) => { setComment(e.target.value); invalidate(); }} />
                </label>
              </div>
            </section>

            {preview && (
              <section className="stock-audit-detail__section stock-correction-preview">
                <h3>{t("stock:valueAdjustment.previewTitle")}</h3>
                <div className="stock-correction-compare">
                  <div><span>{t("stock:valueAdjustment.metric")}</span><span>{t("stock:movementCorrection.before")}</span><span>{t("stock:movementCorrection.after")}</span></div>
                  <div><strong>{t("stock:valueAdjustment.quantity")}</strong><span>{decimal(preview.before?.quantity)} {unit}</span><span>{decimal(preview.after?.quantity)} {unit}</span></div>
                  <div><strong>{t("stock:valueAdjustment.value")}</strong><span>{money(preview.before?.value_net)}</span><span>{money(preview.after?.value_net)}</span></div>
                  <div><strong>{t("stock:valueAdjustment.average")}</strong><span>{decimal(preview.before?.average_cost_net, 6)} PLN/{unit}</span><span>{decimal(preview.after?.average_cost_net, 6)} PLN/{unit}</span></div>
                  <div><strong>{t("stock:valueAdjustment.valueDelta")}</strong><span>—</span><span>{money(preview.value_delta_net)}</span></div>
                </div>
                <p className="stock-correction-policy-note">{t("stock:valueAdjustment.previewHelp")}</p>
              </section>
            )}
          </div>

          <footer className="stock-audit-dialog__footer stock-correction-footer">
            <button type="button" className="stock-btn stock-btn--secondary" disabled={busy} onClick={onClose}>{t("common:actions.cancel")}</button>
            <button type="button" className="stock-btn stock-btn--outline" disabled={busy || loadingContext} onClick={runPreview}>{busy ? t("common:common.loading") : t("stock:valueAdjustment.preview")}</button>
            <button type="button" className="stock-btn stock-btn--primary" disabled={busy || !previewCurrent} onClick={apply}>{t("stock:valueAdjustment.post")}</button>
          </footer>
        </div>
      </FocusLock>
    </div>,
    document.body
  );
}
